// ./src/components/features/CreateEventWizard/websiteBlocksApply.ts
//
// READ what the event's site already carries, and APPLY the website step to its HOME page (ROADMAP A2b #62 P6(d)–(d3)):
// add the ticked blocks, write the description's About block, and put the home page in the host's order. Shared by the
// wizard's website step and the finish screen's "not on your event page yet" nudge, so the two cannot add blocks
// differently. The rules (what is offered, ticked, already there, the order) are the pure `websiteBlocks.ts`.
//
// ⚠ Blocks are built by the PAGE EDITOR's own builder (`emptyBlockForm` + `buildBlockWidgetInput`) — a wizard-made block
//   is one the editor opens, edits and saves like any other. The About block is an ordinary rich-text block (d2).
// ⚠ The home page is the one the site opens at the event's root — the SERVER's rule, mirrored: among PUBLISHED pages, the
//   one flagged home, else the first (`PublicEventCore.getEventSite`). ⚠ Never a draft page: blocks put there would hide
//   exactly what the host asked to show. When no page is published, a published "Home" is created (drafts stay as they
//   are).
// ⚠⚠ The ORDER is written for the WHOLE home page or not at all: renumbering part of a page lands a block between two it
//   was never next to. So both reads are WHOLE (ROADMAP #118: `listForEvent` pages through every row and REFUSES rather
//   than cuts) — a state that comes back at all is the whole site, and a refused read fails the state, so nothing is
//   written. ⓘ Before #118 the blocks were one page of 500 and a `complete` flag held the order back on a full page.
// ⚠ The website step never removes a block. (The Gifts step's `applyGiftLinks`, below, removes only the registry block
//   IT made, and only once nothing at all is left on it.)
import { OP_RESULT_CODES, OpResult } from '@sdflc/api-helpers';
import { STATUSES } from '@sdflc/utils';

import { TextBlockKind, categoryOf, textKindOf, widgetTypeForText } from '@em/blocks';
import { EventPage, EventPageService, PageWidget, PageWidgetService } from '@/utils/api';
import { BlockForm, buildBlockWidgetInput, emptyBlockForm } from '@/pages/private/EventPageEditorPage/blockWidgetForm';

import { GIFT_LINKS_ALIGN, GIFT_LINKS_LAYOUT, mergeGiftButtons } from './giftRegistry';
import { ORDER_STEP, WEBSITE_BLOCK_OPTIONS, WebsiteBlockKey, homeOrder, orderItemOfWidget, orderUpdates, websiteKeysOnSite } from './websiteBlocks';

const LIVE_AND_DRAFT = [STATUSES.ACTIVE, STATUSES.DRAFT];

export interface WebsiteSiteState {
  /** The page the site opens at its root (published), or null when no page is published. */
  homePage: EventPage | null;
  /** Every page, live and draft (a new page's path must not collide with any of them). */
  pages: EventPage[];
  /** Every block on every page (live and draft). */
  widgets: PageWidget[];
  /** The home page's blocks (live and draft), in page order. */
  homeWidgets: PageWidget[];
  /**
   * What the site already carries: every LIVE block on a PUBLISHED page (what guests see), plus ANY block on the home
   * page (a draft block there is the host's own choice — it is never doubled).
   */
  onSite: Set<string>;
}

const byOrder = (a: { orderNo?: number | null }, b: { orderNo?: number | null }): number => (a.orderNo ?? 0) - (b.orderNo ?? 0);

/** Page order: `orderNo`, then creation (the editor's own tie-break). */
const byPageOrder = (a: PageWidget, b: PageWidget): number => byOrder(a, b) || String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? ''));

/** The page the site opens at its root — the server's rule: among PUBLISHED pages, the flagged home, else the first. */
const homeOf = (pages: EventPage[]): EventPage | null => {
  const published = pages.filter((p) => p.status === STATUSES.ACTIVE).sort(byOrder);

  return published.find((p) => p.isHome) ?? published[0] ?? null;
};

/** A path no page of this event uses yet ('home', else 'home-2', …) — a draft page may already hold 'home'. */
const freePath = (pages: EventPage[]): string => {
  const taken = new Set(pages.map((p) => String(p.path ?? '').toLowerCase()));

  for (let n = 1; n < 100; n += 1) {
    const candidate = n === 1 ? 'home' : `home-${n}`;

    if (!taken.has(candidate)) {
      return candidate;
    }
  }

  return `home-${Date.now()}`;
};

/**
 * What the site carries now. `aboutWidgetId` is the description block this wizard made (a text block carries no mark
 * of its own). ⚠ Either read failing fails the whole state — a partial answer would add duplicates.
 */
export const readWebsiteState = async (eventId: string, aboutWidgetId?: string | null): Promise<WebsiteSiteState | OpResult> => {
  const [pagesRes, widgetsRes] = await Promise.all([
    EventPageService.listForEvent(eventId, LIVE_AND_DRAFT),
    PageWidgetService.listForEvent(eventId, LIVE_AND_DRAFT),
  ]);

  if (!pagesRes.didSucceed()) {
    return pagesRes;
  }

  if (!widgetsRes.didSucceed()) {
    return widgetsRes;
  }

  const pages = (Array.isArray(pagesRes.data) ? pagesRes.data : []) as EventPage[];
  const widgets = (Array.isArray(widgetsRes.data) ? widgetsRes.data : []) as PageWidget[];
  const homePage = homeOf(pages);
  const publishedIds = new Set(pages.filter((p) => p.status === STATUSES.ACTIVE).map((p) => p.id));
  const counted = widgets.filter(
    (w) => (w.status === STATUSES.ACTIVE && publishedIds.has(w.eventPageId ?? undefined)) || (!!homePage && w.eventPageId === homePage.id),
  );
  const homeWidgets = homePage ? widgets.filter((w) => w.eventPageId === homePage.id).sort(byPageOrder) : [];

  const onSite = websiteKeysOnSite(counted, (w) => categoryOf(w), aboutWidgetId);

  // ⚠ The About this wizard made is the About wherever it now lives (moved to another page, or hidden) — never made twice.
  if (aboutWidgetId && widgets.some((w) => w.id === aboutWidgetId)) {
    onSite.add('about');
  }

  return {
    homePage,
    pages,
    widgets,
    homeWidgets,
    onSite,
  };
};

/**
 * The page to add blocks to: the one the site opens at its root, else a NEW published "Home" (drafts stay as they are —
 * blocks put on a draft page would hide exactly what the host asked to show). A refusal comes back as the OpResult.
 */
const ensureHomePage = async (eventId: string, state: WebsiteSiteState, homeName: string): Promise<EventPage | OpResult> => {
  if (state.homePage?.id) {
    return state.homePage;
  }

  const created = await EventPageService.create({
    eventId,
    name: homeName,
    path: freePath(state.pages),
    isHome: true,
    showInNav: true,
    accessLevel: 'public',
    status: STATUSES.ACTIVE,
  });

  if (!created.didSucceed()) {
    return created;
  }

  const row = created.getDataFirst() as EventPage | undefined;

  if (!row?.id) {
    return OpResult.fail(OP_RESULT_CODES.EXCEPTION, {}, 'The home page was not created. Please try again.');
  }

  return row;
};

/** The description the About block shows (#62 P6(d2)). */
export interface WebsiteAbout {
  content: string;
  textKind: TextBlockKind;
  /** The About block this wizard made before, if any — it is UPDATED rather than made again. */
  widgetId: string | null;
  /** The host changed the description on the step since it was read — only then is an existing block rewritten. */
  edited: boolean;
}

export interface WebsitePlan {
  /** The ticked blocks to ADD (never one already on the site — re-checked here). */
  keys: readonly WebsiteBlockKey[];
  /** The host's order for the home page (`homeOrder` items), or null to append in catalog order. */
  order?: readonly string[] | null;
  about?: WebsiteAbout | null;
  /** Translates each block's heading. */
  titleOf: (key: WebsiteBlockKey, fallback: string) => string;
  /** Translates each block's subtitle, filling `{{title}}` with the event's title. */
  subtitleOf: (key: WebsiteBlockKey, fallback: string) => string;
  /** The name a page created here gets (translated by the caller). */
  homeName: string;
}

export interface WebsiteApplyResult {
  added: WebsiteBlockKey[];
  /** The About block (made now or before), or null when there is none. */
  aboutWidgetId: string | null;
  /** The home page's order as written (`w:<id>` items), or null when the order was left alone. */
  order: string[] | null;
  /** The blocks made on this run, by key — kept even when a later write failed (a retry must place, not remake, them). */
  created: Partial<Record<WebsiteBlockKey, string>>;
  /** The first refusal, when one stopped the run (what was done before it stays). */
  error: OpResult | null;
}

/**
 * Apply the step to the home page: add the ticked blocks (after whatever is there), write the About block's text, then
 * put the WHOLE home page in the host's order. Re-reads the site first, so a block already on it (added elsewhere since)
 * is skipped rather than doubled.
 */
export const applyWebsitePlan = async (eventId: string, plan: WebsitePlan): Promise<WebsiteApplyResult> => {
  const about = plan.about ?? null;
  const aboutContent = (about?.content ?? '').trim() ? (about?.content ?? '') : '';
  const state = await readWebsiteState(eventId, about?.widgetId);
  const result: WebsiteApplyResult = { added: [], aboutWidgetId: null, order: null, created: {}, error: null };

  if (state instanceof OpResult) {
    return { ...result, error: state };
  }

  const existingAbout = about?.widgetId ? state.widgets.find((w) => w.id === about.widgetId) ?? null : null;

  result.aboutWidgetId = existingAbout?.id ?? null;

  // ⓘ An About with nothing written is never added — a heading over nothing reads as a broken page.
  const wanted = WEBSITE_BLOCK_OPTIONS.filter((o) => plan.keys.includes(o.key) && !state.onSite.has(o.key) && (o.key !== 'about' || !!aboutContent));

  // The description the host edited on the step, onto the About block that already exists (its own settings kept).
  if (existingAbout?.id && about?.edited && aboutContent) {
    const kind = about.textKind;

    if (existingAbout.content !== aboutContent || textKindOf(existingAbout) !== kind) {
      const res = await PageWidgetService.update(existingAbout.id, {
        content: aboutContent,
        widgetType: widgetTypeForText(kind),
        config: { ...(existingAbout.config ?? {}), textKind: kind },
      });

      if (!res.didSucceed()) {
        return { ...result, error: res };
      }
    }
  }

  let home = state.homePage;

  if (!home?.id && wanted.length > 0) {
    const made = await ensureHomePage(eventId, state, plan.homeName);

    if (made instanceof OpResult) {
      return { ...result, error: made };
    }

    home = made;
  }

  if (!home?.id) {
    return result;
  }

  let orderNo = state.homeWidgets.reduce((max, w) => Math.max(max, Number(w.orderNo) || 0), 0);
  const createdByKey = new Map<WebsiteBlockKey, PageWidget>();

  for (const option of wanted) {
    const isAbout = option.key === 'about';
    const form = emptyBlockForm(option.category, isAbout ? about?.textKind ?? 'richtext' : 'richtext');

    form.title = plan.titleOf(option.key, option.blockTitle);

    if (option.blockSubtitle) {
      form.subtitle = plan.subtitleOf(option.key, option.blockSubtitle);
    }

    if (isAbout) {
      form.content = aboutContent;
    }

    Object.assign(form, option.overrides ?? {});

    orderNo += ORDER_STEP;

    const res = await PageWidgetService.create({ ...buildBlockWidgetInput(form), eventPageId: home.id, orderNo });

    if (!res.didSucceed()) {
      return { ...result, error: res };
    }

    const row = (res.getDataFirst() as PageWidget | undefined) ?? { orderNo };

    createdByKey.set(option.key, row);
    result.added.push(option.key);

    if (row.id) {
      result.created[option.key] = row.id;
    }

    if (isAbout && row.id) {
      result.aboutWidgetId = row.id;
    }
  }

  // ── The order (#62 P6(d3)) — the whole home page, or not at all ────────────────────────────────────────────────────
  // ⚠ A block made without its id coming back cannot be placed — renumbering around it would interleave it.
  if (!plan.order || [...createdByKey.values()].some((w) => !w.id)) {
    return result;
  }

  const items = homeOrder(
    state.homeWidgets.map((w) => String(w.id)),
    result.added,
    plan.order,
  );
  const ordered = items
    .map((item) => (item.startsWith('w:') ? item.slice(2) : createdByKey.get(item.slice(2) as WebsiteBlockKey)?.id ?? null))
    .filter((id): id is string => !!id);
  const current = [...state.homeWidgets, ...createdByKey.values()];

  for (const { id, orderNo: next } of orderUpdates(ordered, current)) {
    const res = await PageWidgetService.update(id, { orderNo: next });

    if (!res.didSucceed()) {
      return { ...result, error: res };
    }
  }

  result.order = ordered.map(orderItemOfWidget);

  return result;
};

/** The finish screen's nudge: ADD these blocks at the end of the home page (no order, no description). */
export const addWebsiteBlocks = async (
  eventId: string,
  keys: readonly WebsiteBlockKey[],
  titleOf: (key: WebsiteBlockKey, fallback: string) => string,
  subtitleOf: (key: WebsiteBlockKey, fallback: string) => string,
  homeName: string,
): Promise<WebsiteApplyResult> => applyWebsitePlan(eventId, { keys, titleOf, subtitleOf, homeName });

// ─── #140 — THE GIFT REGISTRY ────────────────────────────────────────────────────────────────────────────────────────

export interface GiftLinksPlan {
  /** The registries to show, as the editor's form holds buttons (`giftLinkButtons`). Empty = no registry block. */
  buttons: BlockForm['links'];
  /** The block this step made before, if any — it is UPDATED (or removed, when every link is gone), never made twice. */
  widgetId: string | null;
  /** The heading a NEW block gets (translated by the caller). */
  title: string;
  /** The name a page created here gets (translated by the caller). */
  homeName: string;
}

export interface GiftLinksResult {
  /** The registry block as it now stands, or null when there is none. */
  widgetId: string | null;
  /** The refusal that stopped it, if any. */
  error: OpResult | null;
}

/**
 * The Gifts step, onto the site: ONE ordinary Link Buttons block holding the registries, made by the page editor's own
 * builder at the END of the home page (the website step's order list then shows it, to be moved like any block).
 * ⚠ It is the block THIS step made (`widgetId`), never a host's own links block. A later visit rewrites only its
 *   web-address buttons (`mergeGiftButtons`): the block's settings, each button's styling and any button the host added
 *   in the page editor to a page / block / action are theirs and kept. It is REMOVED only when nothing at all is left —
 *   a Link Buttons block with no buttons shows the editor's "Add one or more link buttons" prompt to guests.
 * ⚠ A block removed in the editor since is simply made again — the step still says these links are on the page.
 */
export const applyGiftLinks = async (eventId: string, plan: GiftLinksPlan): Promise<GiftLinksResult> => {
  const state = await readWebsiteState(eventId);

  if (state instanceof OpResult) {
    return { widgetId: plan.widgetId, error: state };
  }

  const existing = plan.widgetId ? state.widgets.find((w) => w.id === plan.widgetId) ?? null : null;

  // The buttons exactly as the page editor saves them (`buildBlockWidgetInput` — label, address, icon, style).
  const form = emptyBlockForm('links', 'richtext');

  form.title = plan.title;
  form.links = plan.buttons;
  form.align = GIFT_LINKS_ALIGN;
  form.buttonLayout = GIFT_LINKS_LAYOUT;

  const input = buildBlockWidgetInput(form);
  const rows = input.config?.links ?? [];

  if (existing?.id) {
    const links = mergeGiftButtons(existing.config?.links ?? [], rows);

    if (links.length === 0) {
      const removed = await PageWidgetService.remove(existing.id);

      return removed.didSucceed() ? { widgetId: null, error: null } : { widgetId: existing.id, error: removed };
    }

    const res = await PageWidgetService.update(existing.id, { config: { ...(existing.config ?? {}), links } });

    return res.didSucceed() ? { widgetId: existing.id, error: null } : { widgetId: existing.id, error: res };
  }

  if (rows.length === 0) {
    return { widgetId: null, error: null };
  }

  const home = await ensureHomePage(eventId, state, plan.homeName);

  if (home instanceof OpResult) {
    return { widgetId: null, error: home };
  }

  const last = state.widgets.filter((w) => w.eventPageId === home.id).reduce((max, w) => Math.max(max, Number(w.orderNo) || 0), 0);
  const res = await PageWidgetService.create({ ...input, eventPageId: home.id, orderNo: last + ORDER_STEP });

  if (!res.didSucceed()) {
    return { widgetId: null, error: res };
  }

  const row = res.getDataFirst() as PageWidget | undefined;

  return { widgetId: row?.id ?? null, error: null };
};
